import {createSelector} from 'reselect';
import {GenresState} from 'types';
import {omit} from 'ramda';
import {compose, getFrom} from '@pluto/utils';
import {ContentTypes} from '@pluto/content-types';
import {ContentPartners} from '@pluto/content-partners';
import {CategoriesState} from '@pluto/metadata-categories/CategoriesTypes';
import {WFState} from 'MediaOrders/MediaOrdersTypes';
import {
    ContentItemSearchPayload,
    TextEnum,
    StringTulip,
    SelectOption,
    EnumMapper,
} from '../SearchTypes';

const excludeStatuses = [WFState.INGEST_PENDING, WFState.INGEST_FAILED];

type TextEnums = {
    labPartners: TextEnum;
    languages: TextEnum;
    countryCodes: TextEnum;
    ratings: TextEnum;
    workflowState: TextEnum;
    contentTypes: TextEnum;
    searchableContentItemTypes: TextEnum;
};

interface State {
    contentPartners: ContentPartners;
    contentTypes: ContentTypes;
    contentItemsSearch: ContentItemSearchPayload;
    textEnums: TextEnums;
    genres: GenresState;
    qcMetadataCategories: CategoriesState;
}

const selectSelf = (state: State) => state;

export const deleteProps = <T>(keyList: string[]) => (options: T) =>
    omit(keyList, options);

export const mapEnumEntry = (mapper: EnumMapper) => (options: TextEnum = {}) =>
    Object.entries(options).map(mapper);

export const metaLangMapper = ([value, label]: StringTulip) => ({
    value,
    label: `${label} (${value})`,
});

export const enumMapper = ([value, label]: StringTulip) => ({
    value,
    label,
});

export const optFromLabelMapper = ({label}: {label: string}) => ({
    label,
    value: label,
});

export const getOptionsData = (data: SelectOption[] = []) => ({
    data,
    isLoading: false,
});

export const getEnum = (key: keyof TextEnums) =>
    compose(getFrom(key), getFrom('textEnums'));

const getEnumData = compose(getOptionsData, mapEnumEntry(enumMapper));

export const selectSearchResults = createSelector(
    selectSelf,
    getFrom('contentItemsSearch')
);

export const selectContentPartnersData = createSelector(
    getFrom('contentPartners'),
    ({data = {}, isLoading}) => ({
        data: data.rows || [],
        isLoading,
    })
);

export const selectGenresData = createSelector(
    getFrom('genres'),
    ({genres = [], isLoading}) => ({
        data: genres.map(optFromLabelMapper),
        isLoading,
    })
);

export const selectCategoriesData = createSelector(
    getFrom('qcMetadataCategories'),
    ({data = [], isLoading}) => ({
        data: data.map(optFromLabelMapper),
        isLoading,
    })
);

export const selectMetaLangData = createSelector(
    getEnum('languages'),
    compose(getOptionsData, mapEnumEntry(metaLangMapper))
);

export const selectRatingData = createSelector(getEnum('ratings'), getEnumData);

export const selectRegionsData = createSelector(getEnum('countryCodes'), getEnumData);

export const selectStatusesData = createSelector(
    getEnum('workflowState'),
    compose(getEnumData, deleteProps(excludeStatuses))
);

export const selectLabPartnersData = createSelector(getEnum('labPartners'), getEnumData);

export const selectStatusesObject = createSelector(selectSelf, getEnum('workflowState'));

export const selectContentTypesData = createSelector(
    getEnum('searchableContentItemTypes'),
    getEnumData
);
